// src/utils/SurveyProcessor.js
import {
  MOCK_INSURANCE_COMPANIES,
  MOCK_US_STATES,
} from "../components/PostAuthenticationSurvey";

export const createSimplePanels = (surveyTemplate) => {
  if (
    !surveyTemplate ||
    !surveyTemplate.questions ||
    !surveyTemplate.questions.length
  ) {
    return [];
  }

  // Sort questions by displayOrder if available
  const sortedQuestions = [...surveyTemplate.questions].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  // Create one panel per question
  return sortedQuestions.map((question, index) => ({
    id: `panel_${index}`,
    title: `Question ${index + 1}`,
    description: question.helpText || "",
    questions: [question], // Each panel has just one question
  }));
};
/**
 * Process survey questions into UI panels based on content analysis
 * @param {Object} survey - Raw survey data from API
 * @returns {Array} - Array of panel objects with questions
 */
export function processSurveyIntoPanels(survey) {
  if (!survey || !survey.questions || !Array.isArray(survey.questions)) {
    return [];
  }

  // Define panels with their detection criteria
  const panels = [
    {
      id: "address",
      title: "Address Information",
      description: "Please provide your address details",
      questions: [],
      keywords: ["address", "apt", "unit", "street", "city", "zip"],
    },
    {
      id: "driverInfo",
      title: "Driver Information",
      description: "Tell us about the primary driver",
      questions: [],
      keywords: ["first name", "last name", "middle", "initial", "moved"],
    },
    {
      id: "driverDetails",
      title: "Additional Driver Details",
      description: "Please provide more information about the driver",
      questions: [], // Added missing questions array here
      keywords: [
        "license",
        "marital",
        "residence",
        "education",
        "accident",
        "violation",
        "claim",
        "driving course",
      ],
    },
    {
      id: "vehicleInfo",
      title: "Vehicle Information",
      description: "Tell us about your vehicle",
      questions: [],
      keywords: ["vehicle", "vin", "car", "truck", "auto"],
    },
    {
      id: "policyDetails",
      title: "Policy Details",
      description: "Your quotes are up next, we just need a few final details",
      questions: [],
      keywords: [
        "insurance company",
        "coverage",
        "policy",
        "current company",
        "continuously insured",
        "monitoring",
        "bodily injury",
      ],
    },
  ];

  // Create a fallback panel for questions that don't match any category
  const fallbackPanel = {
    id: "otherDetails",
    title: "Additional Information",
    description: "Additional information for your quote",
    questions: [],
    keywords: [],
  };

  // Make sure we have at least one panel by adding the fallback
  panels.push(fallbackPanel);

  // Sort questions by display order
  const sortedQuestions = [...survey.questions].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  // Distribute questions to panels based on content matching
  sortedQuestions.forEach((question) => {
    const matchedPanel = findMatchingPanel(question, panels);
    if (matchedPanel) {
      matchedPanel.questions.push(question);
    } else {
      // If no match, add to the fallback panel (guaranteed to exist)
      panels[panels.length - 1].questions.push(question);
    }
  });

  // Remove empty panels and keywords property
  return panels
    .filter((panel) => panel.questions && panel.questions.length > 0)
    .map(({ id, title, description, questions }) => ({
      id,
      title,
      description,
      questions,
    }));
}

/**
 * Find the best matching panel for a question based on content
 * @param {Object} question - Survey question
 * @param {Array} panels - Array of panel definitions with keywords
 * @returns {Object|null} - Matching panel or null
 */
function findMatchingPanel(question, panels) {
  const contentToMatch = [question.prompt, question.helpText]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  let bestMatch = null;
  let highestScore = 0;

  panels.forEach((panel) => {
    let score = 0;

    // Check for keyword matches
    panel.keywords.forEach((keyword) => {
      if (contentToMatch.includes(keyword.toLowerCase())) {
        score += 1;
      }
    });

    // If this panel has a better match, update bestMatch
    if (score > highestScore) {
      highestScore = score;
      bestMatch = panel;
    }
  });

  return highestScore > 0 ? bestMatch : null;
}

/**
 * Convert server question type to UI component type
 * @param {Object} question - Server question
 * @returns {string} - UI question type
 */
export function mapQuestionTypeToUIType(question) {
  switch (question.type) {
    case "TEXT":
      if (question.prompt.toLowerCase().includes("address")) {
        return "ADDRESS";
      }
      if (question.prompt.toLowerCase().includes("vin")) {
        return "VIN";
      }
      return "TEXT";

    case "CHOICE":
      if (
        question.options &&
        question.options.length === 2 &&
        question.options.some((o) => o.value === "yes" || o.value === "no")
      ) {
        return "BOOLEAN";
      }
      if (question.multiSelect) {
        return "MULTI_SELECT";
      }
      return "SELECT";

    default:
      return "TEXT";
  }
}

/**
 * Format survey responses for API submission
 * @param {Object} answers - UI component answers
 * @param {Array} questions - Original survey questions
 * @returns {Array} - Formatted responses for API
 */
/**
 * Format survey responses for API submission
 * @param {Object} answers - UI component answers
 * @param {Array} questions - Original survey questions
 * @returns {Array} - Formatted responses for API
 */
export function formatSurveyResponses(answers, questions) {
  return Object.entries(answers)
    .map(([questionId, answer]) => {
      const question = questions.find((q) => q.id === questionId);

      if (!question) {
        return null;
      }

      if (question.type === "TEXT") {
        return {
          type: "TEXT",
          questionId,
          answer: answer.value || "",
        };
      }

      if (question.type === "CHOICE") {
        // Handle predefined lists (US_STATES, INSURANCE_PROVIDERS)
        if (
          question.choiceQuestionType === "US_STATES" ||
          question.choiceQuestionType === "INSURANCE_PROVIDERS"
        ) {
          // For single-select predefined lists
          if (!question.multiSelect) {
            // Find the selected option from our mock data
            let selectedText = "";

            if (question.choiceQuestionType === "US_STATES") {
              const stateOption = MOCK_US_STATES.find(
                (state) => state.value === answer.value
              );
              if (stateOption) selectedText = stateOption.text;
            } else if (question.choiceQuestionType === "INSURANCE_PROVIDERS") {
              if (answer.value === "other") {
                // Handle "Other" selection
                selectedText = answer.otherAnswer || "Other";
              } else {
                const insuranceOption = MOCK_INSURANCE_COMPANIES.find(
                  (company) => company.value === answer.value
                );
                if (insuranceOption) selectedText = insuranceOption.text;
              }
            }

            return {
              type: "CHOICE",
              questionId,
              selections: [
                {
                  text: selectedText,
                  value: answer.value,
                },
              ],
              otherAnswer: answer.otherAnswer || "",
            };
          }
        }

        // Handle regular options from the server
        if (answer.values && Array.isArray(answer.values)) {
          // Multi-select
          return {
            type: "CHOICE",
            questionId,
            selections: answer.values.map((value, index) => {
              const option = question.options.find((o) => o.value === value);
              return {
                choiceOptionId: option?.id || answer.answerIds[index],
                value,
              };
            }),
            otherAnswer: answer.otherAnswer || "",
          };
        } else {
          // Single select
          const option = question.options.find((o) => o.value === answer.value);
          return {
            type: "CHOICE",
            questionId,
            selections: [
              {
                choiceOptionId: option?.id || answer.answerId,
                value: answer.value,
              },
            ],
            otherAnswer: answer.otherAnswer || "",
          };
        }
      }

      return null;
    })
    .filter(Boolean);
}
